'use client'

import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { GooglePlayBadge } from './StoreBadges'

/**
 * Mobile-only "Download our app" popup that appears 1s after the
 * homepage loads, at most once every COOLDOWN_MS per browser.
 *
 * Why this shape, not a centered modal:
 *  - On a phone, a bottom-sheet style popup is the platform-native
 *    pattern for download CTAs (Spotify, Instagram, Pinterest use it).
 *    A centered dialog on a 375px screen ends up covering the whole
 *    page and feels like an interstitial.
 *  - On desktop, the hero already shows badges prominently — adding a
 *    popup there is redundant. So this component is hidden ≥ 901px.
 *
 * Why time-based localStorage, not sessionStorage:
 *  - sessionStorage only resets when a tab is closed. A user who
 *    leaves the homepage open for hours would never be reminded.
 *  - A 2-hour cooldown re-engages "tab left open" visitors while not
 *    nagging on refresh / quick back-navigation.
 *  - The key is namespaced ('sa.') and stores a timestamp so we can
 *    tune the window without changing the key name.
 */

const STORAGE_KEY = 'sa.appPopupLastShown'
const COOLDOWN_MS = 2 * 60 * 60 * 1000 // 2 hours
const MOBILE_QUERY = '(max-width: 900px)'

export default function AppDownloadPopup({
  delayMs = 1000,
}: {
  delayMs?: number
}) {
  // `null` until we've decided whether to show — prevents any flash
  // before the session-storage / matchMedia checks run on the client.
  const [open, setOpen] = useState<boolean | null>(null)

  useEffect(() => {
    // Server can't read localStorage / matchMedia, so the decision is
    // made entirely on the client. Avoids hydration mismatches because
    // the initial render is always `null` (renders nothing).
    if (typeof window === 'undefined') return

    const isMobile = window.matchMedia(MOBILE_QUERY).matches
    if (!isMobile) {
      setOpen(false)
      return
    }

    // Cooldown check: parse the stored timestamp; if it's within the
    // cooldown window, stay silent. Anything older (or missing /
    // invalid) lets the popup fire again.
    let withinCooldown = false
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const last = Number(raw)
        if (Number.isFinite(last) && Date.now() - last < COOLDOWN_MS) {
          withinCooldown = true
        }
      }
    } catch {
      // Private browsing can throw on storage access; treat as out of cooldown.
    }
    if (withinCooldown) {
      setOpen(false)
      return
    }

    const timer = window.setTimeout(() => {
      setOpen(true)
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()))
      } catch {
        /* ignore quota / private-mode errors */
      }
    }, delayMs)

    return () => window.clearTimeout(timer)
  }, [delayMs])

  // Escape closes too, mirroring the App Store "Coming Soon" modal.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (open !== true) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-popup-title"
      aria-describedby="app-popup-desc"
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        animation: 'appPopupFade 200ms ease-out',
      }}
    >
      {/* Bottom sheet — stop propagation so taps inside don't dismiss. */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '480px',
          background: 'var(--bg-card)',
          color: 'var(--text-primary)',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          border: '1px solid var(--border)',
          borderBottom: 'none',
          padding: '20px 22px calc(24px + env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 -16px 40px rgba(0, 0, 0, 0.4)',
          position: 'relative',
          animation: 'appPopupSlideUp 280ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* Drag handle (decorative — visual cue this is dismissible) */}
        <div
          aria-hidden="true"
          style={{
            width: '40px',
            height: '4px',
            borderRadius: '4px',
            background: 'var(--border)',
            margin: '0 auto 14px',
          }}
        />

        {/* Close button (top right) */}
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close download prompt"
          style={{
            position: 'absolute',
            top: '14px',
            right: '14px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={18} />
        </button>

        {/* Centered stack: icon on top, then title, then description.
            Mobile bottom-sheet pattern looks better centered when the
            sheet's content is short. */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '10px', marginBottom: '16px', padding: '0 8px' }}>
          <img
            src="/favicon-96x96.png"
            alt=""
            width={56}
            height={56}
            decoding="async"
            style={{ borderRadius: '12px' }}
          />
          <h2
            id="app-popup-title"
            style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '18px',
              fontWeight: 800,
              margin: 0,
              lineHeight: 1.2,
              color: 'var(--text-primary)',
            }}
          >
            Get the SarvamAstro App
          </h2>
          <p
            id="app-popup-desc"
            style={{
              fontSize: '13px',
              color: 'var(--text-muted)',
              margin: 0,
              lineHeight: 1.5,
              maxWidth: '320px',
            }}
          >
            Birth chart, Kundli matching & daily horoscope — free on Google Play.
          </p>
        </div>

        {/* The badge itself — GooglePlayBadge is already an <a> opening
            the real Play Store URL in a new tab, so clicking it both
            navigates and (because the user moves away) the popup
            disappears with the page. */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }} onClick={() => setOpen(false)}>
          <GooglePlayBadge height={52} />
        </div>

        <p style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', margin: '12px 0 0' }}>
          Tap anywhere outside to dismiss
        </p>
      </div>
    </div>
  )
}
